import React, { useState, useEffect } from 'react';
import './Notification.css';
import { RefreshCw, Search, Globe, Percent, Building2, Type, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { notificationApi } from '../../../config/notification-api';
import { getCountryName, getLanguageName } from '../../../constants/constants';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import CreateNotificationQueModal from './CreateNotificationQueModal';

const Notification = ({ adminData }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_NOTIFICATION_QUE);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [todayQues, setTodayQues] = useState([]);
  const [pastQues, setPastQues] = useState([]);
  const [isPastExpanded, setIsPastExpanded] = useState(false);
  const [refreshingQueId, setRefreshingQueId] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });

  const [loading, setLoading] = useState(true);
  const [isPastLoading, setIsPastLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchQues(currentPage);
  }, [currentPage, isPastExpanded]);

  const fetchQues = async (page = 1) => {
    try {
      if (isPastExpanded) {
        setIsPastLoading(true);
      }

      const todayResponse = await notificationApi.fetchAllNotificationQues({
        type: 'today'
      });

      let pastResponse = null;
      if (isPastExpanded) {
        pastResponse = await notificationApi.fetchAllNotificationQues({
          type: 'past',
          page: page,
          limit: 10,
        });
      }

      if (todayResponse && todayResponse.success) {
        setTodayQues(todayResponse.data.ques || []);
      } else {
        console.error('Today Ques API Error:', todayResponse?.msg || 'Unknown error');
        setTodayQues([]);
      }

      if (pastResponse && pastResponse.success) {
        setPastQues(pastResponse.data.ques || []);
        setPagination(pastResponse.data.pagination);
      } else if (isPastExpanded) {
        console.error('Past Ques API Error:', pastResponse?.msg || 'Unknown error');
        setPastQues([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNext: false,
          hasPrev: false
        });
      }

    } catch (error) {
      console.error('Error fetching ques:', error);
      setTodayQues([]);
      setPastQues([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
      setIsPastLoading(false);
    }
  };

  const handleRefreshQue = async (queId) => {
    try {
      setRefreshingQueId(queId);
      const response = await notificationApi.fetchNotificationQueById(queId);

      if (response.success) {
        setTodayQues(prev =>
          prev.map(q => q._id === queId ? {
            ...q,
            ...response.data,
          } : q)
        );

        setPastQues(prev =>
          prev.map(q => q._id === queId ? {
            ...q,
            ...response.data,
          } : q)
        );
      }
    } catch (error) {
      console.error('Error refreshing que:', error);
    } finally {
      setRefreshingQueId(null);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNext) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.hasPrev) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleTogglePast = () => {
    const newExpandedState = !isPastExpanded;
    setIsPastExpanded(newExpandedState);

    if (newExpandedState) {
      setCurrentPage(1);
      setPastQues([]);
    } else {
      setPastQues([]);
    }
  };

  const handleCreateNotification = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    fetchQues(currentPage);
  };

  const queCard = (que) => {
    return (
      <div key={que._id} className={`notification-que-card notification-${que.status || ''}`}>
        <div className="notification-que-card-header">
          <h3 className="notification-que-title">{que.title}</h3>

          {que.status && (
            <span className={`notification-que-status notification-${que.status}`}>
              {que.status === 'in_progress' ? 'In Progress' :
                que.status.charAt(0).toUpperCase() + que.status.slice(1)}
            </span>
          )}

          {(que.status === 'pending' || que.status === 'in_progress') && (
            <button
              className="notification-refresh-btn"
              onClick={() => handleRefreshQue(que._id)}
              disabled={refreshingQueId === que._id}
            >
              <RefreshCw size={16} className={refreshingQueId === que._id ? 'notification-spinning' : ''} />
            </button>
          )}
        </div>

        <div className="notification-que-card-body">
          <div className="notification-que-content">
            {que.image && (
              <div className="notification-que-image-box">
                <img src={que.image} alt="notification" />
              </div>
            )}
            <div>
              <p className="notification-que-body-text">{que.bodyText}</p>

              <div className="notification-que-meta">
                {que.isCrmCreated && (
                  <div className="notification-meta-tag">
                    <Building2 size={14} />
                    <span>CRM</span>
                  </div>
                )}
                {que.couponName && (
                  <div className="notification-meta-tag">
                    <Percent size={14} />
                    <span>{que.couponName}</span>
                  </div>
                )}
                {que.country && (
                  <div className="notification-meta-tag">
                    <Globe size={14} />
                    <span>{getCountryName(que.country)}</span>
                  </div>
                )}
                {que.language && (
                  <div className="notification-meta-tag">
                    <Type size={14} />
                    <span>{getLanguageName(que.language)}</span>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {que.status === 'in_progress' && (
          <div className="notification-que-stats">
            <div className="notification-stat-item">
              <span className="notification-stat-label">Total Count</span>
              <span className="notification-stat-value">{que.totalCount || 0}</span>
            </div>
            <div className="notification-stat-item">
              <span className="notification-stat-label">Success</span>
              <span className="notification-stat-value notification-success">{que.successCount || 0}</span>
            </div>
            <div className="notification-stat-item">
              <span className="notification-stat-label">Failed</span>
              <span className="notification-stat-value notification-failed">{que.failedCount || 0}</span>
            </div>
            <div className="notification-stat-item">
              <span className="notification-stat-label">Progress</span>
              <span className="notification-stat-value">{(que.percentage ?? 0).toFixed(2)}%</span>
            </div>
          </div>
        )}

        {que.status === 'completed' && (
          <div className="notification-que-stats">
            <div className="notification-stat-item">
              <span className="notification-stat-label">Total Count</span>
              <span className="notification-stat-value">{que.totalCount || 0}</span>
            </div>
            <div className="notification-stat-item notification-with-percentage">
              <span className="notification-stat-label">Success</span>
              <div style={{ display: "flex" }}>
                <span className="notification-stat-value notification-success">{que.successCount || 0}</span>
                <span className="notification-percentage-detail">({(que.successPercentage ?? 0).toFixed(2)}%)</span>
              </div>
            </div>
            <div className="notification-stat-item notification-with-percentage">
              <span className="notification-stat-label">Failed</span>
              <div style={{ display: "flex" }}>
                <span className="notification-stat-value notification-failed">{que.failedCount || 0}</span>
                <div className="notification-percentage-detail">({(que.failedPercentage ?? 0).toFixed(2)}%)</div>
              </div>
            </div>
          </div>
        )}

        {que.status === 'pending' && (
          <div className="notification-que-stats notification-pending">
            <div className="notification-stat-item">
              <span className="notification-stat-label">Total Count</span>
              <span className="notification-stat-value">{que.totalCount || 0}</span>
            </div>
          </div>
        )}

        <div className="notification-que-dates">
          <div className="notification-que-date">
            Created: {que.createdAt || "-"}
          </div>
          {que.status === 'in_progress' && que.startedAt && (
            <div className="notification-que-date">
              Started: {que.startedAt || "-"}
            </div>
          )}
          {que.status === 'completed' && que.startedAt && (
            <div className="notification-que-date">
              Started: {que.startedAt || "-"}
            </div>
          )}
          {que.status === 'completed' && que.completedAt && (
            <div className="notification-que-date">
              Completed: {que.completedAt || '-'}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="notification-page">
      <div className="notification-page-header">
        <div className="notification-page-title">
          <h1>Notification Ques</h1>
          <p>Send notifications to your customers</p>
        </div>
        {canCreate && (
          <button className="notification-create-btn" onClick={handleCreateNotification}>
            Create Notification
          </button>
        )}
      </div>

      <div className="notification-que-section">
        <div className="notification-que-group">
          <h2 className="notification-que-group-title">Today's Ques</h2>
          {loading ? (
            <div className="notification-loading-state">Loading Today's Ques...</div>
          ) : todayQues.length === 0 ? (
            <div className="notification-empty-state">No ques created today</div>
          ) : (
            <div className="notification-que-grid">
              {todayQues.map((que) => queCard(que))}
            </div>
          )}
        </div>

        <div className="notification-que-group notification-past-que-group">
          <div className="notification-past-que-header" onClick={handleTogglePast}>
            <h2 className="notification-que-group-title">Past Ques</h2>
            <button className="notification-toggle-btn">
              {isPastExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </button>
          </div>

          {isPastExpanded && (
            <>
              {isPastLoading ? (
                <div className="notification-loading-state">Loading Past Ques...</div>
              ) : pastQues.length === 0 ? (
                <div className="notification-empty-state">No past ques found</div>
              ) : (
                <>
                  <div className="notification-que-grid">
                    {pastQues.map((que) => queCard(que))}
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className="notification-pagination">
                      <button
                        onClick={handlePrevPage}
                        disabled={!pagination.hasPrev || isPastLoading}
                        className="notification-pagination-btn"
                      >
                        Previous
                      </button>
                      <span className="notification-pagination-info">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={!pagination.hasNext || isPastLoading}
                        className="notification-pagination-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

      </div >

      <CreateNotificationQueModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />

    </div >
  );
};

export default Notification;